import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Loader2, Plus, Trash2, AlertTriangle, X, MessageSquare, Sparkles, PanelLeftOpen, PanelLeftClose, Paperclip } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown from "react-markdown";
import * as chatApi from "@/service/chat";
import type { ChatConversation, ChatMessage } from "@/service/chat";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";

function formatRelativeTime(value: string | Date): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const diffMs = Date.now() - date.getTime();
  const absMs = Math.abs(diffMs);

  const units = [
    { limit: 60_000, divisor: 1_000, unit: "second" as const },
    { limit: 3_600_000, divisor: 60_000, unit: "minute" as const },
    { limit: 86_400_000, divisor: 3_600_000, unit: "hour" as const },
    { limit: 2_592_000_000, divisor: 86_400_000, unit: "day" as const },
    { limit: 31_536_000_000, divisor: 2_592_000_000, unit: "month" as const },
    { limit: Number.POSITIVE_INFINITY, divisor: 31_536_000_000, unit: "year" as const },
  ];

  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const { divisor, unit } = units.find((entry) => absMs < entry.limit) ?? units[0];
  const amount = Math.round(diffMs / divisor);
  return formatter.format(-amount, unit);
}

interface ChatPanelProps {
  assessmentId: string;
  cardContextLabel?: string;
  cardContextData?: string;
  onClose: () => void;
}

export function ChatPanel({ assessmentId, cardContextLabel, cardContextData, onClose }: ChatPanelProps) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [activeConvId, setActiveConvId] = useState<string | null>(null);
  const [input, setInput] = useState("");
  const [attachedContext, setAttachedContext] = useState<string | null>(null);
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [quotaWarning, setQuotaWarning] = useState<{ show: boolean; percent: number }>({
    show: false,
    percent: 0,
  });
  const [showHistoryView, setShowHistoryView] = useState(true);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const MAX_MSG_LENGTH = 2500;

  const { data: quotaData } = useQuery({
    queryKey: ["chatQuota"],
    queryFn: () => chatApi.getQuotaUsage(),
  });

  const { data: conversations = [] } = useQuery<ChatConversation[]>({
    queryKey: ["conversations", assessmentId],
    queryFn: () => chatApi.listConversations(assessmentId),
  });

  const { data: messages = [], isLoading: loadingMessages } = useQuery<ChatMessage[]>({
    queryKey: ["messages", activeConvId],
    queryFn: () => chatApi.getMessages(activeConvId!),
    enabled: !!activeConvId,
  });

  const createConv = useMutation({
    mutationFn: () => chatApi.createConversation(assessmentId),
    onSuccess: (conv) => {
      qc.invalidateQueries({ queryKey: ["conversations", assessmentId] });
      setActiveConvId(conv.id);
      setShowHistoryView(false);
    },
  });

  const deleteConv = useMutation({
    mutationFn: (id: string) => chatApi.deleteConversation(id),
    onSuccess: (_data, deletedId) => {
      qc.invalidateQueries({ queryKey: ["conversations", assessmentId] });
      if (activeConvId === deletedId) {
        setActiveConvId(null);
        setShowHistoryView(true);
      }
    },
  });

  const sendMsg = useMutation({
    mutationFn: (params: { conversationId: string; content: string; context?: string }) =>
      chatApi.sendMessage(
        params.conversationId,
        params.content,
        assessmentId,
        params.context
      ),
    onSuccess: (result, variables) => {
      if (result.quotaExceeded) {
        setQuotaExceeded(true);
        return;
      }
      if (result.error) {
        toast({ variant: "destructive", title: "Error", description: result.error });
        return;
      }
      if (result.data?.quotaWarning) {
        setQuotaWarning({ show: true, percent: result.data.percentUsed ?? 0 });
      }
      setInput("");
      setAttachedContext(null);
      qc.invalidateQueries({ queryKey: ["messages", variables.conversationId] });
      qc.invalidateQueries({ queryKey: ["conversations", assessmentId] });
      qc.invalidateQueries({ queryKey: ["chatQuota"] });
    },
    onError: () => {
      toast({ variant: "destructive", title: "Error", description: "Failed to send message. Please try again." });
    },
  });

  useEffect(() => {
    setAttachedContext(null);
  }, [cardContextData]);

  useEffect(() => {
    // Scroll to bottom when messages change or when switching conversations
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sendMsg.isPending, activeConvId]);

  const isOverLimit = input.length > MAX_MSG_LENGTH;

  const startDraftConversation = () => {
    setActiveConvId(null);
    setAttachedContext(null);
    setInput("");
    setShowHistoryView(false);
  };

  const handleSend = async () => {
    if (!input.trim() || sendMsg.isPending || createConv.isPending || isOverLimit) return;

    let conversationId = activeConvId;
    if (!conversationId) {
      const created = await createConv.mutateAsync();
      conversationId = created.id;
    }

    sendMsg.mutate({
      conversationId,
      content: input,
      context: attachedContext ?? undefined,
    });
  };

  const activeConv = conversations.find((c) => c.id === activeConvId);
  const isHistoryView = showHistoryView;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      <div className="flex-1 min-h-0 flex flex-col min-w-0 bg-gradient-to-b from-background to-muted/20">
        <div className="px-5 py-3 border-b flex items-center justify-between bg-background/80 backdrop-blur-sm sticky top-0 z-10">
          <div className="flex items-center gap-2 min-w-0">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => setShowHistoryView((prev) => !prev)}
              title={isHistoryView ? "Close history" : "Open history"}
            >
              {isHistoryView ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeftOpen className="h-4 w-4" />}
            </Button>
            <Sparkles className="h-5 w-5 text-primary" />
            <span className="font-semibold">{isHistoryView ? "History" : "AI Assistant"}</span>
            {!isHistoryView && activeConv && (
              <span className="text-muted-foreground text-sm truncate max-w-[200px]">
                - {activeConv.title || "Chat"}
              </span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={startDraftConversation}
              disabled={createConv.isPending}
              title={"New Conversation"}
            >
              <Plus className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {(quotaExceeded || quotaWarning.show) && (
          <div
            className={cn(
              "px-5 py-2.5 text-sm flex items-center gap-3 border-b shrink-0",
              quotaExceeded
                ? "bg-destructive/10 text-destructive"
                : "bg-yellow-50/80 text-yellow-900"
            )}
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>
              {quotaExceeded
                ? "AI quota exhausted. Contact your administrator."
                : `${quotaWarning.percent}% of AI quota used`}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto h-7 px-2 text-xs"
              onClick={() => {
                setQuotaWarning({ show: false, percent: 0 });
                if (quotaExceeded) setQuotaExceeded(false);
              }}
            >
              {"Dismiss"}
            </Button>
          </div>
        )}

        <div className="flex-1 min-h-0">
        {isHistoryView ? (
          <div className="h-full overflow-y-auto px-3 py-3">
            {conversations.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                <MessageSquare className="h-12 w-12 mb-4 opacity-40" />
                <p className="text-lg font-medium">{"No conversations yet."}</p>
                <p className="text-sm mt-2">{"Click + to start one."}</p>
              </div>
            ) : (
              conversations.map((conv) => {
                const isActive = activeConvId === conv.id;
                const lastMsg = conv.lastMessageAt
                  ? formatRelativeTime(conv.lastMessageAt)
                  : "New";

                return (
                  <div
                    key={conv.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setActiveConvId(conv.id);
                      setShowHistoryView(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setActiveConvId(conv.id);
                        setShowHistoryView(false);
                      }
                    }}
                    className={cn(
                      "group flex items-center gap-3 w-full px-3 py-3 mb-1 text-left cursor-pointer rounded-lg transition-colors hover:bg-muted/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
                      isActive ? "bg-muted/80" : "bg-transparent"
                    )}
                  >
                    <MessageSquare
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isActive ? "text-primary" : "text-muted-foreground"
                      )}
                    />

                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <p
                        className={cn(
                          "text-sm font-medium truncate",
                          isActive ? "text-foreground" : "text-foreground/90"
                        )}
                      >
                        {conv.title || "New Conversation"}
                      </p>
                      {lastMsg && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {lastMsg}
                        </p>
                      )}
                    </div>

                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 shrink-0 opacity-100 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConv.mutate(conv.id);
                      }}
                      title={"Delete conversation"}
                      aria-label={"Delete conversation"}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })
            )}
          </div>
        ) : (
          <ScrollArea className="h-full px-5 py-6">
            {!activeConvId ? (
              <div className="h-full flex flex-col items-center justify-center text-center text-muted-foreground">
                <MessageSquare className="h-12 w-12 mb-4 opacity-40" />
                <p className="text-lg font-medium">{"Select or start a conversation"}</p>
                <p className="text-sm mt-2">{"Ask anything about your ESG assessment"}</p>
              </div>
            ) : loadingMessages ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary/70" />
              </div>
            ) : (
              messages.map((msg) => (
                <div
                  key={msg.id}
                  className={cn(
                    "mb-6 flex gap-4",
                    msg.role === "user" ? "justify-end" : "justify-start"
                  )}
                >
                  {msg.role !== "user" && (
                    <div className="shrink-0 mt-1">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <Sparkles className="h-4 w-4 text-primary" />
                      </div>
                    </div>
                  )}

                  <div
                    className={cn(
                      "max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed shadow-sm",
                      msg.role === "user"
                        ? "bg-primary text-primary-foreground rounded-tr-none"
                        : "bg-card border prose prose-sm dark:prose-invert max-w-none rounded-tl-none"
                    )}
                  >
                    {msg.role === "assistant" ? (
                      <ReactMarkdown
                        components={{
                          //todo add code block copy buttons etc later if wanted
                        }}
                      >
                        {msg.content}
                      </ReactMarkdown>
                    ) : (
                      msg.content
                    )}
                  </div>

                  {msg.role === "user" && (
                    <div className="shrink-0 mt-1">
                      <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <span className="text-xs font-medium">You</span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}

            {sendMsg.isPending && (
              <div className="flex justify-start mb-6">
                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center animate-pulse">
                  <Loader2 className="h-4 w-4 animate-spin text-primary" />
                </div>
                <div className="ml-4 rounded-2xl bg-card border px-4 py-3">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </ScrollArea>
        )}
        </div>

        {!isHistoryView && !quotaExceeded && (
          <div className="border-t bg-background/95 backdrop-blur-md p-4 sticky bottom-0 z-10">
            <div className="max-w-4xl mx-auto flex flex-col gap-2">
              {attachedContext && cardContextLabel && (
                <div className="flex items-center px-1">
                  <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 text-primary border border-primary/20 px-3 py-1.5 text-xs font-medium transition-colors">
                    <Paperclip className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate max-w-[200px] sm:max-w-[300px]">
                      {cardContextLabel}
                    </span>
                    <button
                      onClick={() => setAttachedContext(null)}
                      className="ml-1 rounded-full p-1 hover:bg-primary/20 text-primary/70 hover:text-primary transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
                      aria-label="Remove context"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                </div>
              )}

              <div
                className={cn(
                  "relative flex flex-col rounded-2xl border bg-background shadow-sm transition-shadow focus-within:ring-1 focus-within:ring-primary",
                  isOverLimit ? "border-destructive focus-within:ring-destructive" : "border-input"
                )}
              >
                <Textarea
                  placeholder={"Ask about your assessment..."}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  disabled={sendMsg.isPending}
                  rows={1}
                  className="min-h-[52px] resize-none border-0 shadow-none focus-visible:ring-0 px-4 py-3.5 text-sm bg-transparent"
                />

                <div className="flex items-center justify-between px-2 pb-2 pt-1">
                  <div className="flex items-center">
                    {cardContextData && !attachedContext && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 rounded-full text-muted-foreground hover:text-foreground"
                            onClick={() => setAttachedContext(cardContextData)}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="top">
                          {"Add current card data to message"}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>

                  <Button
                    size="icon"
                    className={cn(
                      "h-8 w-8 rounded-full transition-all",
                      input.trim() && !isOverLimit ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                    )}
                    onClick={handleSend}
                    disabled={sendMsg.isPending || !input.trim() || isOverLimit}
                  >
                    {sendMsg.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
                        <path d="M3.5 5.5L20.5 12L3.5 18.5L6.5 12L3.5 5.5Z" />
                      </svg>
                    )}
                  </Button>
                </div>
              </div>

              <div className="flex items-center justify-between px-2 text-[11px]">
                <span
                  className={cn(
                    "transition-colors",
                    isOverLimit ? "text-destructive font-semibold" : "text-muted-foreground"
                  )}
                >
                  {input.length} / {MAX_MSG_LENGTH}
                </span>

                {quotaData && (
                  <span
                    className={cn(
                      "font-medium transition-colors",
                      quotaData.percentUsed >= 90
                        ? "text-destructive"
                        : quotaData.percentUsed >= 75
                          ? "text-amber-500"
                          : "text-muted-foreground"
                    )}
                  >
                    {`${quotaData.percentUsed}% used`}
                  </span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
